import React, { useState, useEffect } from "react";
import { useWarehouseContext } from "./WarehouseProvider";
import { StepHeader } from "./components/StepHeader";
import { FullscreenModal } from "./components/FullscreenModal";
import { TakePhoto } from "./components/TakePhoto";
import { ReactComponent as CameraSvg } from "./svgs/camera.svg";
import { ReactComponent as PhotoSvg } from "./svgs/photo.svg";
import { ReactComponent as WarningSvg } from "./svgs/warning.svg";
import { ReactComponent as ErrorSvg } from "./svgs/error.svg";
import { PhotoThumbnailsModal } from "./components/PhotoThumbnailsModal";
import {
  WarehouseButtonsFooter,
  WarehouseButton,
  WarehouseButtonGroup,
} from "./components/WarehouseButtonsFooter";
import { useSteps } from "./useSteps";

export const RejectContents = () => {
  const [error] = useState("");
  const [showCamera, setShowCamera] = useState(false);
  const [reason, setReason] = useState("");
  const [showPhotoModal, setShowPhotoModal] = useState(false);

  const {
    rejectedContentsPictures,
    addRejectContentsPicture,
    deleteRejectContentsPicture,
    rejectCategory,
    setRejectCategory,
  } = useWarehouseContext();

  const { showRejection, cancelReject } = useSteps();

  const onPhotoTaken = (picture) => {
    setShowCamera(false);
    addRejectContentsPicture(picture);
  };

  const onCancel = () => {
    setShowCamera(false);
  };

  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        cancelReject();
      }
    };

    document.addEventListener("keydown", handleEscapeKey);

    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [cancelReject]);

  return (
    <>
      <FullscreenModal>
        <div className="flex flex-col h-full">
          <StepHeader
            rightSlot={
              <button
                onClick={() => setShowPhotoModal(true)}
                className="relative px-2 hover:bg-[#282F44]/20 rounded-lg transition-colors aspect-square"
                disabled={rejectedContentsPictures.length === 0}
              >
                <PhotoSvg className="fill-[#A1A7C2] w-6" />
                {rejectedContentsPictures?.length > 0 && (
                  <sup className="absolute top-[unset] bottom-[0.5rem] flex items-center justify-center aspect-square right-0 rounded-full w-4 h-4 bg-[#C12960] text-white text-xs text-center">
                    {rejectedContentsPictures.length}
                  </sup>
                )}
              </button>
            }
          />

          <div className="flex-1 flex flex-col items-center justify-center px-4">
            <div className="w-full max-w-4xl">
              <h1 className="text-white text-4xl font-medium text-center mb-2">
                Reject
              </h1>
              <p className="text-[#A1A7C2] text-xl text-center mb-8">
                Select a rejection reason to continue
              </p>

              <div className="flex gap-4 mb-8">
                <button
                  onClick={() => setRejectCategory("hazardous")}
                  className={`flex-1 p-6 rounded-2xl border-2 transition-all ${
                    rejectCategory === "hazardous"
                      ? "border-transparent bg-[#173991]"
                      : "border-[#2A3654] bg-transparent hover:border-[#3A4664]"
                  }`}
                >
                  <div className="flex items-center justify-center gap-3">
                    <WarningSvg className="w-6 h-6 fill-[#FFA500]" />
                    <span className="text-white text-xl">Hazardous</span>
                  </div>
                </button>

                <button
                  onClick={() => setRejectCategory("fraudulent")}
                  className={`flex-1 p-6 rounded-2xl border-2 transition-all ${
                    rejectCategory === "fraudulent"
                      ? "border-transparent bg-[#173991]"
                      : "border-[#2A3654] bg-transparent hover:border-[#3A4664]"
                  }`}
                >
                  <div className="flex items-center justify-center gap-3">
                    <ErrorSvg className="w-6 h-6 fill-[#FF5757]" />
                    <span className="text-white text-xl">Fraudulent</span>
                  </div>
                </button>

                <button
                  onClick={() => setRejectCategory("other")}
                  className={`flex-1 p-6 rounded-2xl border-2 transition-all ${
                    rejectCategory === "other"
                      ? "border-transparent bg-[#173991]"
                      : "border-[#2A3654] bg-transparent hover:border-[#3A4664]"
                  }`}
                >
                  <span className="text-white text-xl">Other</span>
                </button>
              </div>

              <textarea
                id="reason"
                placeholder="Rejection reason"
                rows={3}
                className="rounded-2xl p-4 border-2 focus:outline-none focus:ring-0 w-full border-[#2A3654] bg-transparent text-white text-lg placeholder-[#6B7280] resize-none"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>

          <WarehouseButtonsFooter error={error}>
            <WarehouseButtonGroup columns={3}>
              <WarehouseButton variant="default" onClick={cancelReject}>
                Cancel
              </WarehouseButton>
              <WarehouseButton
                variant="primary"
                onClick={() => setShowCamera(true)}
              >
                <div className="flex items-center justify-center gap-2">
                  <CameraSvg className="fill-white w-5 h-5" />
                  <span>Take Photo of Rejected Material</span>
                </div>
              </WarehouseButton>
              <WarehouseButton
                variant="primary"
                onClick={showRejection}
                disabled={
                  !rejectCategory || rejectedContentsPictures.length === 0
                }
              >
                Continue
              </WarehouseButton>
            </WarehouseButtonGroup>
          </WarehouseButtonsFooter>

          {showCamera && (
            <TakePhoto
              title="Reject Take Back"
              onPhotoTaken={onPhotoTaken}
              onCancel={onCancel}
              retakeButtonText="Retake Photo of Rejected Material"
            />
          )}
        </div>
      </FullscreenModal>

      <PhotoThumbnailsModal
        isOpen={showPhotoModal}
        onClose={() => setShowPhotoModal(false)}
        photos={rejectedContentsPictures}
        onDeletePhoto={deleteRejectContentsPicture}
        title="Rejection Photos"
      />
    </>
  );
};
